import { useCallback, useMemo } from 'react';
import {
  DocumentationFilter,
  EnvironmentFilter,
  IncludesOrMissing,
  PictureFilter,
} from '@retail/products/types';
import {
  DocumentationFilterDataKey,
  EnvironmentDataFilterKey,
  FilterObjects,
  insightFilters,
  InsightFilters,
  PictureFilterDataKey,
  SelectedFilters,
} from '@retail/products/utils';
import { useFilters } from '@retail/products/hooks';
import { useFilterOptions } from './useFilterOptions';
import { useSearchParamState } from '@shared/hooks';

const environmentFiltersParamKey = 'environmentFilters';
const documentationFiltersParamKey = 'documentationFilters';
const pictureFiltersParamKey = 'pictureFilters';
const includeNotIncludedDataParamKey = 'includeNotIncludedData';
const includedOrMissingDataParamKey = 'includedOrMissingField';
const climateDataValueParamKey = 'climateData';

interface InsightValueFilters {
  includeNotIncludedItemData: boolean;
  includedOrMissingFieldValue: IncludesOrMissing;
  environmentValuesSelected: EnvironmentDataFilterKey[];
  documentationValuesSelected: DocumentationFilterDataKey[];
  pictureValuesSelected: PictureFilterDataKey[];
  climateDataA1ToA3Included: boolean;
  environmentValuesInterface: EnvironmentFilter;
  documentationValuesInterface: DocumentationFilter;
  pictureValuesInterface: PictureFilter;
}

export function useInsightReportFilters() {
  const { filters, resetFilters } = useFilters<InsightFilters>(insightFilters);
  const { searchParams, handleFilterChange } = useSearchParamState();

  const { suppliers, productGroups, productGroupHierarchy } = useFilterOptions({
    selectedSupplierNumbers: filters.supplierNumber ? [filters.supplierNumber] : undefined,
    selectedProductGroupNumbers: filters.productGroupNumber
      ? [filters.productGroupNumber]
      : undefined,
  });
  const selectedSupplier = useMemo(
    () =>
      suppliers.find((supplier) => supplier.supplierNumber.toString() === filters.supplierNumber) ??
      null,
    [filters.supplierNumber, suppliers]
  );
  const selectedProductGroup = useMemo(
    () => productGroups.find((pg) => pg.groupNumber === filters.productGroupNumber) ?? null,
    [filters.productGroupNumber, productGroups]
  );

  const includeNotIncludedItemData = useMemo(
    () => !!searchParams.get(includeNotIncludedDataParamKey),
    [searchParams]
  );

  const toggleIncludeNotIncludedItemData = useCallback(
    () =>
      handleFilterChange({
        filterType: includeNotIncludedDataParamKey,
        value: includeNotIncludedItemData ? undefined : 'true',
      }),
    [handleFilterChange, includeNotIncludedItemData]
  );

  const includedOrMissingFieldValue = useMemo(
    () => (searchParams.get(includedOrMissingDataParamKey) ?? 'INCLUDES') as IncludesOrMissing,
    [searchParams]
  );

  const toggleIncludedOrMissingFieldValue = useCallback(
    () =>
      handleFilterChange({
        filterType: includedOrMissingDataParamKey,
        value: includedOrMissingFieldValue === 'INCLUDES' ? 'MISSING' : undefined,
      }),
    [handleFilterChange, includedOrMissingFieldValue]
  );

  const getValuesSelected = useCallback(
    (paramKey: string) => {
      const values = searchParams.get(paramKey);
      if (values) {
        return JSON.parse(values);
      } else {
        return null;
      }
    },
    [searchParams]
  );

  const environmentValuesSelected: EnvironmentDataFilterKey[] = useMemo(
    () => getValuesSelected(environmentFiltersParamKey) ?? [],
    [getValuesSelected]
  );

  const environmentValuesInterface: EnvironmentFilter = useMemo(() => {
    const environmentFilters: EnvironmentFilter = {};
    environmentValuesSelected.forEach((field) => {
      environmentFilters[field] = true;
    });
    return environmentFilters;
  }, [environmentValuesSelected]);

  const documentationValuesSelected: DocumentationFilterDataKey[] = useMemo(
    () => getValuesSelected(documentationFiltersParamKey) ?? [],
    [getValuesSelected]
  );

  const documentationValuesInterface: DocumentationFilter = useMemo(() => {
    const docuFilters: DocumentationFilter = {};
    documentationValuesSelected.forEach((field) => {
      docuFilters[field as DocumentationFilterDataKey] = true;
    });
    return docuFilters;
  }, [documentationValuesSelected]);

  const pictureValuesSelected: PictureFilterDataKey[] = useMemo(
    () => getValuesSelected(pictureFiltersParamKey) ?? [],
    [getValuesSelected]
  );

  const pictureValuesInterface: PictureFilter = useMemo(() => {
    const pictureFilters: PictureFilter = {};
    pictureValuesSelected.forEach((field) => {
      pictureFilters[field as PictureFilterDataKey] = true;
    });
    return pictureFilters;
  }, [pictureValuesSelected]);

  const handleSetValuesSelected = useCallback(
    (selectedField: string, currentFields: string[], searchParamKey: string) => {
      const updatedFields = JSON.stringify(
        currentFields.includes(selectedField)
          ? currentFields.filter((field) => field !== selectedField)
          : [...currentFields, selectedField]
      );
      handleFilterChange({
        filterType: searchParamKey,
        value: updatedFields,
      });
    },
    [handleFilterChange]
  );

  const updateEnvironmentFilterValues = useCallback(
    (selectedValue: EnvironmentDataFilterKey) =>
      handleSetValuesSelected(selectedValue, environmentValuesSelected, environmentFiltersParamKey),
    [environmentValuesSelected, handleSetValuesSelected]
  );

  const updateDocumentationFilterValues = useCallback(
    (selectedValue: DocumentationFilterDataKey) =>
      handleSetValuesSelected(
        selectedValue,
        documentationValuesSelected,
        documentationFiltersParamKey
      ),
    [documentationValuesSelected, handleSetValuesSelected]
  );

  const updatePictureFilterValues = useCallback(
    (selectedValue: PictureFilterDataKey) =>
      handleSetValuesSelected(selectedValue, pictureValuesSelected, pictureFiltersParamKey),
    [handleSetValuesSelected, pictureValuesSelected]
  );

  const climateDataA1ToA3Included = useMemo(
    () => !!searchParams.get(climateDataValueParamKey),
    [searchParams]
  );

  const toggleClimateDataA1ToA3Included = useCallback(
    () =>
      handleFilterChange({
        filterType: climateDataValueParamKey,
        value: climateDataA1ToA3Included ? undefined : 'true',
      }),
    [climateDataA1ToA3Included, handleFilterChange]
  );

  const handleReset = useCallback(
    () =>
      resetFilters([
        includeNotIncludedDataParamKey,
        environmentFiltersParamKey,
        documentationFiltersParamKey,
        pictureFiltersParamKey,
        climateDataValueParamKey,
      ]),

    [resetFilters]
  );
  return {
    suppliers,
    productGroupHierarchy,
    selectedFilters: {
      ...filters,
      selectedSupplier,
      selectedProductGroup,
      inAssortment: filters.inAssortment === 'UNSPECIFIED' ? 'NO' : filters.inAssortment,
      includeNotIncludedItemData,
      environmentValuesInterface,
      environmentValuesSelected,
      documentationValuesInterface,
      documentationValuesSelected,
      pictureValuesInterface,
      pictureValuesSelected,
      climateDataA1ToA3Included,
      includedOrMissingFieldValue,
    } satisfies SelectedFilters<InsightFilters> &
      FilterObjects<InsightFilters> &
      InsightValueFilters,
    setFilters: {
      toggleIncludeNotIncludedItemData,
      updateEnvironmentFilterValues,
      updateDocumentationFilterValues,
      updatePictureFilterValues,
      toggleClimateDataA1ToA3Included,
      toggleIncludedOrMissingFieldValue,
    },
    handleFilterChange,
    resetFilters: handleReset,
  };
}
